// @intent: Cleanup-safe frame/timer helpers for generated playable code
// @flow: PixiPlayableAPI exposes api.loop -> runtime cleanup scope owns RAF/timer handles
// @stale: Keep synchronized with PlayableLoop in @mintyads/playable-api

import type { PlayableLoop, PlayableLoopFrame } from '@mintyads/playable-api';
import type { RuntimeCleanupScope } from './runtimeCleanup';

export interface RuntimeLoopOptions {
  readonly cleanupScope?: RuntimeCleanupScope | undefined;
  readonly now?: (() => number) | undefined;
  readonly requestFrame?: ((callback: FrameRequestCallback) => number) | undefined;
  readonly cancelFrame?: ((handle: number) => void) | undefined;
  readonly setTimeout?: ((callback: () => void, ms: number) => unknown) | undefined;
  readonly clearTimeout?: ((handle: unknown) => void) | undefined;
  readonly setInterval?: ((callback: () => void, ms: number) => unknown) | undefined;
  readonly clearInterval?: ((handle: unknown) => void) | undefined;
}

function once(cleanup: () => void): () => void {
  let called = false;
  return () => {
    if (called) return;
    called = true;
    cleanup();
  };
}

function track(cleanupScope: RuntimeCleanupScope | undefined, cleanup: () => void): () => void {
  const tracked = once(cleanup);
  cleanupScope?.add(tracked);
  return tracked;
}

function normalizeMs(value: number): number {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

type TimerArg = number | (() => void);

function normalizeTimerArgs(method: 'delay' | 'interval', first: TimerArg, second: TimerArg): {
  readonly ms: number;
  readonly handler: () => void;
} {
  if (typeof first === 'number' && typeof second === 'function') {
    return { ms: normalizeMs(first), handler: second };
  }
  if (typeof first === 'function' && typeof second === 'number') {
    return { ms: normalizeMs(second), handler: first };
  }
  throw new TypeError(`api.loop.${method} expects (ms, handler) or (handler, ms)`);
}

function defaultRequestFrame(callback: FrameRequestCallback): number {
  if (typeof globalThis.requestAnimationFrame === 'function') {
    return globalThis.requestAnimationFrame(callback);
  }
  return globalThis.setTimeout(() => { callback(performance.now()); }, 16) as unknown as number;
}

function defaultCancelFrame(handle: number): void {
  if (typeof globalThis.cancelAnimationFrame === 'function') {
    globalThis.cancelAnimationFrame(handle);
    return;
  }
  globalThis.clearTimeout(handle);
}

export function createRuntimeLoop(options: RuntimeLoopOptions = {}): PlayableLoop {
  const now = options.now ?? (() => performance.now());
  const requestFrame = options.requestFrame ?? defaultRequestFrame;
  const cancelFrame = options.cancelFrame ?? defaultCancelFrame;
  const setDelay = options.setTimeout ?? ((callback: () => void, ms: number) => globalThis.setTimeout(callback, ms));
  const clearDelay = options.clearTimeout ?? ((handle: unknown) => {
    globalThis.clearTimeout(handle as ReturnType<typeof globalThis.setTimeout>);
  });
  const setEvery = options.setInterval ?? ((callback: () => void, ms: number) => globalThis.setInterval(callback, ms));
  const clearEvery = options.clearInterval ?? ((handle: unknown) => {
    globalThis.clearInterval(handle as ReturnType<typeof globalThis.setInterval>);
  });

  const add = (handler: (frame: PlayableLoopFrame) => void) => {
    let active = true;
    let frameId: number | null = null;
    const startedAt = now();
    let previousAt = startedAt;
    const tick: FrameRequestCallback = (timestamp) => {
      if (!active) return;
      const nowMs = Number.isFinite(timestamp) ? timestamp : now();
      handler({
        deltaMs: Math.max(0, nowMs - previousAt),
        elapsedMs: Math.max(0, nowMs - startedAt),
        nowMs,
      });
      previousAt = nowMs;
      frameId = requestFrame(tick);
    };
    frameId = requestFrame(tick);
    return track(options.cleanupScope, () => {
      active = false;
      if (frameId !== null) cancelFrame(frameId);
    });
  };

  const delay = ((first: TimerArg, second: TimerArg) => {
    const { ms, handler } = normalizeTimerArgs('delay', first, second);
    let fired = false;
    const handle = setDelay(() => {
      fired = true;
      handler();
    }, ms);
    return track(options.cleanupScope, () => {
      if (!fired) clearDelay(handle);
    });
  }) as PlayableLoop['delay'];

  const interval = ((first: TimerArg, second: TimerArg) => {
    const { ms, handler } = normalizeTimerArgs('interval', first, second);
    const handle = setEvery(handler, ms);
    return track(options.cleanupScope, () => {
      clearEvery(handle);
    });
  }) as PlayableLoop['interval'];

  return {
    add,
    raf: add,
    delay,
    interval,
  };
}
